import React from 'react';
import styled, { css } from 'styled-components';

import { formatSupportDate, formatSupportDateTime } from '../../supportFormat';
import {
  ResponseCellKind,
  ResponseTableCell,
  ResponseTableRow,
  SupportAction,
  SupportTicketTableBlock
} from '../../types';
import { ResponseActionButton } from '../ResponseActionButton';
import { useResponseAction } from '../responseActions';
import {
  ResponseHead,
  ResponseIntro,
  ResponsePanel,
  ResponsePill,
  ResponseReply
} from '../responsePrimitives';
import { responseTone, responseType, rsColor, rsv } from '../responseScale';

// 6265:4227: inset 20 from the panel, fixed shares of its width.
const TableBody = styled.div`
  padding: ${rsv.space('tableBody')};
`;

const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  th {
    padding: ${rsv.space('tableHead')};
    border-bottom: 1px solid ${rsColor.border};
    color: ${rsv.color('label')};
    text-align: left;
    ${responseType('tableHead')}
  }
`;

// 6265:4241: 50 tall, ruled except the last; the row is the "open this ticket" target.
const Row = styled.tr<{ $active: boolean }>`
  cursor: ${({ $active }) => ($active ? 'pointer' : 'default')};

  &:not(:last-child) > td {
    border-bottom: 1px solid ${rsColor.border};
  }

  &:hover > td {
    background: ${({ $active }) => ($active ? rsColor.inset : 'transparent')};
  }

  &:focus-visible {
    outline: 2px solid ${rsColor.link};
    outline-offset: -2px;
  }
`;

// 6604:1488-1498: ID Bold text, status Semi Bold in its tone, the rest Regular secondary.
const Cell = styled.td<{ $kind: ResponseCellKind; $ink: string }>`
  box-sizing: border-box;
  height: ${rsv.size('tableRow')}px;
  padding: ${rsv.space('tableCell')};
  color: ${({ $ink }) => $ink};
  overflow-wrap: anywhere;
  vertical-align: middle;
  ${({ $kind }) => {
    switch ($kind) {
      case 'id':
        return responseType('cell', 700);
      case 'status':
        return responseType('cellStatus');
      // 6604:1492: the 23-tall pill is centred in the 50 row, not padded below it.
      case 'pill':
        return css`
          padding-top: 0;
          padding-bottom: 0;
          ${responseType('cell')}
        `;
      default:
        return responseType('cell');
    }
  }}
`;

// 6265:4244: the footer link, 6 under the table.
const TableFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: ${rsv.space('tableFooter')};

  > * + * {
    margin-left: ${rsv.space('buttonGap')}px;
  }
`;

const cellInk = (kind: ResponseCellKind, cell: ResponseTableCell | null): string => {
  if (cell?.tone) {
    return responseTone(cell.tone).ink;
  }
  return kind === 'id' || kind === 'status' ? rsColor.text : rsColor.secondary;
};

const cellText = (kind: ResponseCellKind, value: string): string => {
  switch (kind) {
    case 'date':
      return formatSupportDate(value);
    case 'datetime':
      return formatSupportDateTime(value);
    default:
      return value;
  }
};

const CellValue: React.FC<{ kind: ResponseCellKind; cell: ResponseTableCell }> = ({
  kind,
  cell
}) => {
  if (kind === 'pill') {
    const tone = responseTone(cell.tone);
    return (
      <ResponsePill $ink={tone.ink} $fill={tone.fill}>
        {cell.value}
      </ResponsePill>
    );
  }
  return <>{cellText(kind, cell.value)}</>;
};

const TicketRow: React.FC<{
  row: ResponseTableRow;
  block: SupportTicketTableBlock;
  onAction: (action: SupportAction) => void;
}> = ({ row, block, onAction }) => {
  const action = row.action && row.action.disabled !== true ? row.action : null;
  const choose = () => {
    if (action) {
      onAction(action);
    }
  };
  return (
    <Row
      $active={action !== null}
      tabIndex={action ? 0 : undefined}
      aria-label={action?.label}
      data-testid="orbit-response-table-row"
      data-row-id={row.id}
      onClick={choose}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          choose();
        }
      }}
    >
      {block.columns.map(column => {
        const cell = row.cells[column.key] ?? null;
        return (
          <Cell key={column.key} $kind={column.kind} $ink={cellInk(column.kind, cell)}>
            {cell ? <CellValue kind={column.kind} cell={cell} /> : null}
          </Cell>
        );
      })}
    </Row>
  );
};

/** Figma 6421:9962, the "Active Support Tickets" table (6265:4222). */
const SupportTicketTable: React.FC<{ block: SupportTicketTableBlock }> = ({ block }) => {
  const emit = useResponseAction(block);
  return (
    <ResponseReply
      $column="replyColumnNarrow"
      data-testid="orbit-response-support_ticket_table"
      data-block-id={block.id}
    >
      {block.intro ? <ResponseIntro>{block.intro}</ResponseIntro> : null}
      <ResponsePanel>
        <ResponseHead title={block.title} icon={block.icon}>
          {block.head_actions?.map(action => (
            <ResponseActionButton key={action.id} action={action} onAction={emit} />
          ))}
        </ResponseHead>
        <TableBody>
          <Table>
            <colgroup>
              {block.columns.map(column => (
                <col
                  key={column.key}
                  style={column.width ? { width: `${column.width * 100}%` } : undefined}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                {block.columns.map(column => (
                  <th key={column.key} scope="col">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map(row => (
                <TicketRow key={row.id} row={row} block={block} onAction={emit} />
              ))}
            </tbody>
          </Table>
        </TableBody>
        {block.actions.length > 0 ? (
          <TableFooter>
            {block.actions.map(action => (
              <ResponseActionButton key={action.id} action={action} onAction={emit} />
            ))}
          </TableFooter>
        ) : null}
      </ResponsePanel>
    </ResponseReply>
  );
};

export default SupportTicketTable;
